import { getUserId } from '../../helpers/helperMethods';
import {
  GA4_MEASUREMENT_ID_ENV,
  GA4_SETUP_TEST_EVENT,
  GA4_TRACKER_ENABLED_ENV,
} from './ga4Constants';
import { initGa4ConsentSync } from './ga4Consent';
import { isGa4DebugMode } from './ga4Env';
import { getGtag } from './gtag';

let initialized = false;
let teardownConsentSync: (() => void) | null = null;

export const isGa4Enabled = (): boolean =>
  String(process.env[GA4_TRACKER_ENABLED_ENV] || '').toLowerCase() === 'true';

export const getGa4MeasurementId = (): string | null => {
  const id = process.env[GA4_MEASUREMENT_ID_ENV]?.trim();
  return id || null;
};

/** Configure the dedicated frontend GA4 property. Legacy gtag (G-V03TEP7J74) is unchanged in index.html. */
export const initGa4 = (): void => {
  if (initialized || !isGa4Enabled()) return;

  const measurementId = getGa4MeasurementId();
  if (!measurementId) {
    if (isGa4DebugMode()) {
      console.warn(
        '[ga4] REACT_APP_GA4_TRACKER_ENABLED is true but REACT_APP_GA4_MEASUREMENT_ID is missing'
      );
    }
    return;
  }

  const gtag = getGtag();
  if (!gtag) {
    if (isGa4DebugMode()) {
      console.warn('[ga4] window.gtag is not available — check public/index.html consent stub');
    }
    return;
  }

  teardownConsentSync = initGa4ConsentSync();

  const userId = getUserId();

  gtag('config', measurementId, {
    send_page_view: false,
    ...(isGa4DebugMode() ? { debug_mode: true } : {}),
    ...(userId ? { user_id: String(userId) } : {}),
  });

  initialized = true;

  if (isGa4DebugMode()) {
    trackGa4Event(GA4_SETUP_TEST_EVENT, {
      source: 'web',
      page_location: window.location.href,
    });
  }
};

/** Fire a custom event on the dedicated GA4 property only (via send_to). */
export const trackGa4Event = (
  eventName: string,
  params: Record<string, unknown> = {}
): void => {
  if (!initialized || !isGa4Enabled()) return;

  const measurementId = getGa4MeasurementId();
  const gtag = getGtag();
  if (!measurementId || !gtag) return;

  gtag('event', eventName, {
    send_to: measurementId,
    ...(isGa4DebugMode() ? { debug_mode: true } : {}),
    ...params,
  });
};

/** For tests — reset module init state. */
export const __resetGa4ForTests = (): void => {
  if (teardownConsentSync) {
    teardownConsentSync();
    teardownConsentSync = null;
  }
  initialized = false;
};
